import { globalTranslate } from '@pfsc/shared/locale/app.i18n';
import { ref } from 'vue';

/**
 * Initial configuration for a confirmation dialog.
 * @typedef {object} InitialDialogConfig
 * @property {string=} confirmButtonText - The text for the confirmation button.
 * @property {string=} cancelButtonText - The text for the cancel button.
 * @property {string} title - The title of the confirmation dialog.
 * @property {string} content - The content of the confirmation dialog.
 * @property {boolean=} shouldHideCancelButton - Whether cancel button should be hidden.
 * @property {(string | number)=} width - Width of the dialog, defaults to fit the content.
 * @property {boolean=} shouldCenterTitle - Whether the title should be centered.
 * @property {() => void} onConfirmClick - Callback trigger when confirm button clicked.
 * @property {() => void} [onCancelClick] - Callback trigger when cancel button clicked.
 */
const initialDialogConfig = {
  confirmButtonText: globalTranslate('confirmDialog.confirmButtonText'),
  cancelButtonText: globalTranslate('confirmDialog.cancelButtonText'),
  title: '',
  content: '',
  shouldHideCancelButton: false,
  width: 'auto',
  shouldCenterTitle: false,
  onConfirmClick: () => {},
  onCancelClick: () => {},
};

export const confirmDialogConfig = ref({
  ...initialDialogConfig,
  isShowing: false,
});

/** Hide confirm dialog. */
function closeDialog() {
  confirmDialogConfig.value.isShowing = false;
}

/**
 * Show confirm dialog to user interface.
 * @param {InitialDialogConfig} config Dialog configuration.
 */
function showConfirmDialogWith(config) {
  confirmDialogConfig.value = {
    ...initialDialogConfig,
    ...config,
    isShowing: true,
  };

  confirmDialogConfig.value.onConfirmClick = () => {
    config.onConfirmClick();
    closeDialog();
  };
}

/**
 * Show common confirm dialog.
 * @param {() => void} callback Callback.
 */
function showCommonConfirmDialog(callback) {
  showConfirmDialogWith({
    title: globalTranslate('commonConfirmDialog.title'),
    content: globalTranslate('commonConfirmDialog.content'),
    onConfirmClick: callback,
  });
}

export const confirmDialogSerVice = {
  showConfirmDialogWith,
  showCommonConfirmDialog,
  closeDialog,
};
